/** 관리자에 의한 비밀번호 강제 재설정 모달 (M12 / U3-c). */

import { useEffect, useState } from "react";
import { AlertCircle, Loader2, X } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";

import type { AdminUserOut } from "@/lib/api/admin_users";
import { ApiError } from "@/lib/api/client";

import { useResetAdminUserPassword } from "./useAdminUsers";

const PASSWORD_MIN_LEN = 4;

interface Props {
  user: AdminUserOut;
  onClose: () => void;
}

export default function ResetPasswordModal({ user, onClose }: Props) {
  const { t } = useTranslation();
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const mutation = useResetAdminUserPassword();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const tooShort = newPw.length > 0 && newPw.length < PASSWORD_MIN_LEN;
  const mismatch = confirm.length > 0 && newPw !== confirm;
  const valid = newPw.length >= PASSWORD_MIN_LEN && newPw === confirm;

  const apiError =
    mutation.error instanceof ApiError ? mutation.error.detail : (mutation.error?.message ?? null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || mutation.isPending) return;
    mutation.mutate({ userId: user.id, newPassword: newPw }, { onSuccess: () => onClose() });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 py-8"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-900">{t("admin.users.reset.title")}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label={t("common.actions.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {/*
             * <Trans> — ``admin.users.reset.warning`` 키에 ``<strong>{{email}}
             * </strong>`` 인라인 마크업이 포함되어 있다. ``values`` 로 email
             * 을 주입하고 ``components.strong`` 으로 emphasis span 매핑.
             */}
            <Trans
              i18nKey="admin.users.reset.warning"
              values={{ email: user.email }}
              components={{ strong: <span className="font-medium" /> }}
            />
            <br />
            {t("admin.users.reset.warningBody")}
          </div>

          <div>
            <label htmlFor="rp-new" className="block text-xs font-medium text-slate-700">
              {t("admin.users.reset.newLabel", { count: PASSWORD_MIN_LEN })}
            </label>
            <input
              id="rp-new"
              type="password"
              autoComplete="new-password"
              required
              minLength={PASSWORD_MIN_LEN}
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={mutation.isPending}
              aria-invalid={tooShort}
            />
            {tooShort ? (
              <p className="mt-1 text-xs text-red-600">
                {t("admin.users.reset.tooShort", { count: PASSWORD_MIN_LEN })}
              </p>
            ) : null}
          </div>

          <div>
            <label htmlFor="rp-confirm" className="block text-xs font-medium text-slate-700">
              {t("admin.users.reset.confirmLabel")}
            </label>
            <input
              id="rp-confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={mutation.isPending}
              aria-invalid={mismatch}
            />
            {mismatch ? (
              <p className="mt-1 text-xs text-red-600">{t("admin.users.reset.mismatch")}</p>
            ) : null}
          </div>

          {apiError ? (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{apiError}</span>
            </div>
          ) : null}

          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              disabled={mutation.isPending}
            >
              {t("common.actions.cancel")}
            </button>
            <button
              type="submit"
              disabled={!valid || mutation.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {mutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : null}
              {t("admin.users.reset.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
